import {
	AdAccountDTO,
	FacebookAdsCampaignDTO,
	GoogleAdsCampaignDTO,
	ProtectionInfoDetectionType,
	ProtectionInfoDTO
} from 'api-models'

/**
 * Campaigns for the demo website (DEV-2422 §3).
 *
 * Demo assets and ad accounts are real records (`demo_website` / `demo_account`, seeded
 * alongside the generated clicks in `core/runtime/system/lib/clicks.ts`), but **nothing
 * anywhere creates campaigns for them** — which is why `setAssetForId` skipped the
 * campaign fetch for demo at all. Without these, Connections shows ad accounts that
 * expand to nothing, the campaign drawer and the campaign-status filter have no rows,
 * and the AI Protection page's own-settings panel can never list a campaign.
 *
 * Synthesised on the client rather than seeded: demo is a presentation environment, and
 * these must not become rows in a shared database.
 *
 * The set deliberately spans all three status buckets, so the status marks and the
 * Enabled-only default filter are both visible in demo, and the first campaign of each
 * account owns its protection so the inherit/own split is visible too.
 */

type DemoCampaign = GoogleAdsCampaignDTO | FacebookAdsCampaignDTO

/** Per platform, since each carries its own status enum. */
const STATUSES = {
	GADS: ['ENABLED', 'ENABLED', 'ENABLED', 'PAUSED', 'REMOVED'],
	FADS: ['ACTIVE', 'ACTIVE', 'ACTIVE', 'PAUSED', 'ARCHIVED'],
	BADS: ['Active', 'Active', 'Active', 'Paused', 'Deleted']
} as const

const NAMES = [
	'Brand – Exact',
	'Generic – Search',
	'Competitor – Exact',
	'Dynamic Search',
	'Retargeting – Display'
]

/**
 * Settings for the one campaign per account that does NOT inherit.
 *
 * `inherit: false` is the whole point — `ownsProtection()` tests exactly that field, so
 * it is what puts the campaign in the AI Protection page's "{n} with their own settings"
 * panel and opens its drawer with the tick cleared.
 *
 * The values deliberately differ from the website defaults (monitoring instead of block,
 * custom tuning instead of AI, a narrowed region, half the signals off). An override that
 * matched its parent would satisfy the code and demonstrate nothing: the point of the
 * panel is to show settings that diverge.
 */
const OWN_PROTECTION: ProtectionInfoDTO = {
	enabled: true,
	inherit: false,
	mode: 4,
	detection_type: ProtectionInfoDetectionType.CUSTOM_TUNING,
	blocking_mode: 'monitoring',
	geo: true,
	region: ['US', 'CA'],
	region_mode: null,
	vpn: false,
	multi_target: false,
	ip_range: true,
	bots: true,
	bounced: true,
	interaction: false,
	converted: false,
	non_converting: false,
	placements: false,
	proxy: true,
	ip_whitelist: []
}

/**
 * The FIRST campaign of each ad account carries its own protection, so the inherit/own
 * split is demonstrable — every other campaign inherits.
 *
 * A fresh object per campaign, never one shared constant: the drawers hand this straight
 * to `protectionToSettings`, and a shared reference would let one demo campaign's edit
 * show up on every other one.
 */
export const demoCampaignsFor = (adAccount: AdAccountDTO): DemoCampaign[] => {
	const platform = (adAccount.platform ?? 'GADS') as keyof typeof STATUSES
	const statuses = STATUSES[platform] ?? STATUSES.GADS

	return NAMES.map((name, index) => ({
		id: `${adAccount.id}-demo-campaign-${index + 1}`,
		ad_account_id: adAccount.id,
		name,
		status: statuses[index],
		protection: index === 0 ? { ...OWN_PROTECTION } : null,
		rules_settings: null
	})) as DemoCampaign[]
}

export const demoCampaigns = (adAccounts: AdAccountDTO[]): DemoCampaign[] =>
	adAccounts.flatMap(demoCampaignsFor)
